import React, {createContext, useContext, useReducer, useMemo} from "react";
import {Verification} from "../models";
import {LoadingState} from "../helper/utils";
import {getIntents} from "../services/requests";


interface State {
    message?: {desc: string, title: string}
    intents: Verification[]
    loadingState: LoadingState
}
type Action =
| {type: "set_intents", payload: Verification[]}
| {type: "add_intent"; payload: Verification}
| {type: "remove_intent"; payload: string}
| {type: "set_loading_state"; payload: LoadingState}
| {type: "set_error", payload?: {desc: string, title: string}}

const VerificationContext = createContext<ReturnType<typeof useVerificationValue> | undefined>(undefined);
const initialState: State = {intents: [], loadingState: LoadingState.None};

function createRequestActions(dispatch: React.Dispatch<Action>) {
    return {
        addIntent: (payload: Verification) => dispatch(
            {type: "add_intent", payload}
        ),
        clearError: () => dispatch({type: "set_error"}),
        removeIntent: (payload: string) => dispatch(
            {type: "remove_intent", payload}
        ),
        setIntents: (payload: Verification[]) => dispatch(
            {type: "set_intents", payload}
        ),
        setRequeststate: (payload: LoadingState) => dispatch(
            {type: "set_loading_state", payload}
        ),
    };
}

function reducer(state: State, action: Action): State {
    switch (action.type) {
        case "set_loading_state": return {...state, loadingState: action.payload};
        case "set_error": return {...state, message: action.payload};
        case "set_intents": return {...state, intents: action.payload};
        case "add_intent": {
            const intents = state.intents.concat([action.payload]);
            return {...state, intents};
        }
        case "remove_intent": {
            const intents = state.intents.filter(
                (v) => v.VerificationID !== action.payload
            );
            return {...state, intents};
        }
        default: return state;
    }
}

function useVerificationValue() {
    const [state, dispatch] = useReducer(reducer, initialState);
    const actions = useMemo(() => createRequestActions(dispatch), [dispatch]);
    const canCreate = useMemo(function () {
        return state.intents.filter((v) => v.intent === "CREATION").length <= 0;
    }, [state.intents]);
    const canAct = useMemo(function () {
        return state.intents.filter((v) => v.intent !== "CREATION").length <= 0;
    }, [state.intents]);
    function has(id: string) {
        return state.intents.filter((i) => i.VerificationID === id).length > 0;
    }
    function get(id: string): Verification | undefined {
        const res = state.intents.filter((i) => i.VerificationID === id);
        if (res[0]) {
            return res[0];
        }
    }
    async function fetchIntents() {
        let res;
        actions.setRequeststate(LoadingState.Pending);
        res = await getIntents();
        if (res.type === "success") {
            dispatch({type: "set_intents", payload: res.value});
            actions.setRequeststate(LoadingState.Success);
        } else {
            actions.setRequeststate(LoadingState.Error);
            dispatch({type: "set_error", payload: {
                desc: res.message,
                title: "Intent Fetching Error"
            }});
        }
    }
    return {actions, canAct, canCreate, dispatch, fetchIntents, get, has, state};
}

export function VerificationProvider(props: {children: React.ReactNode}) {
    const value = useVerificationValue();
    return <VerificationContext.Provider value={value}>
        {props.children}
    </VerificationContext.Provider>;
}

export function useVerification() {
    const context = useContext(VerificationContext);
    if (!context) {
        throw new Error("useVerification must be used within a VerificationProvider");
    }
    return context;
}
